/*!
 * Copyright © 2026 Nail Alliance - North America, Inc.
 * Authors: See snippets/copyright-notices.liquid in the theme.
 * No other use is authorized without a written contract.
 */

(() => {
  class EnhancementPromos extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.slides = [...this.querySelectorAll('[data-promo-slide]')];
      this.buttons = [...this.querySelectorAll('[data-promo-index]')];
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.delay = Math.max(2000, Number(this.dataset.delay) || 4000);

      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-promo-index]');
        if (button) this.show(Number(button.dataset.promoIndex));
      }, { signal });
      this.addEventListener('mouseenter', () => this.stop(), { signal });
      this.addEventListener('mouseleave', () => this.start(), { signal });
      this.addEventListener('focusin', () => this.stop(), { signal });
      this.addEventListener('focusout', () => this.start(), { signal });
      document.addEventListener('visibilitychange', () => document.hidden ? this.stop() : this.start(), { signal });
      this.show(this.index);
      this.start();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.stop();
    }

    show(index) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
      this.buttons.forEach((button, buttonIndex) => {
        button.setAttribute('aria-current', buttonIndex === this.index ? 'true' : 'false');
      });
    }

    start() {
      this.stop();
      if (this.slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.timer = window.setInterval(() => this.show(this.index + 1), this.delay);
    }

    stop() {
      window.clearInterval(this.timer);
    }
  }

  class EnhancementColorBrowser extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.range = this.querySelector('[data-color-range]');
      this.label = this.querySelector('[data-color-label]');
      this.buttons = [...this.querySelectorAll('[data-color-index]')];
      this.groups = [...this.querySelectorAll('[data-color-group]')];
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-color-index]');
        if (button) this.show(Number(button.dataset.colorIndex));
      }, { signal });
      this.range?.addEventListener('input', () => this.show(Number(this.range.value)), { signal });
      this.show(0);
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    show(index) {
      if (!this.groups.length) return;
      const nextIndex = Math.min(this.groups.length - 1, Math.max(0, index));
      this.groups.forEach((group, groupIndex) => {
        const active = groupIndex === nextIndex;
        group.hidden = !active;
        group.inert = !active;
      });
      this.buttons.forEach((button, buttonIndex) => {
        button.setAttribute('aria-selected', String(buttonIndex === nextIndex));
      });
      if (this.range) this.range.value = String(nextIndex);
      if (this.label) {
        const selectedButton = this.buttons[nextIndex];
        this.label.textContent = selectedButton?.querySelector('.visually-hidden')?.textContent?.trim() || '';
      }
    }
  }

  class EnhancementSizeGroup extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.panels = [...this.querySelectorAll('[data-size-panel]')];
      this.buttons = [...this.querySelectorAll('[data-size-index]')];
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-size-index]');
        if (button) this.show(Number(button.dataset.sizeIndex));
      }, { signal: this.controller.signal });
      this.show(0);
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    show(index) {
      this.panels.forEach((panel, panelIndex) => {
        const active = panelIndex === index;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
        panel.inert = !active;
      });
      this.buttons.forEach((button, buttonIndex) => {
        button.setAttribute('aria-selected', String(buttonIndex === index));
      });
    }
  }

  class EnhancementSteps extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.groups = [...this.querySelectorAll('[data-step-group]')];
      this.buttons = [...this.querySelectorAll('[data-step-group-index]')];
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-step-group-index]');
        if (button) this.show(Number(button.dataset.stepGroupIndex));
      }, { signal: this.controller.signal });
      this.show(0);
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    show(index) {
      this.groups.forEach((group, groupIndex) => {
        const active = groupIndex === index;
        group.hidden = !active;
        group.inert = !active;
        group.classList.toggle('is-active', active);
        if (active) group.querySelector('product-instructions')?.show?.(0);
      });
      this.buttons.forEach((button, buttonIndex) => {
        button.setAttribute('aria-selected', String(buttonIndex === index));
      });
    }
  }

  class BuilderVideoGallery extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.buttons = [...this.querySelectorAll('[data-builder-video-index]')];
      this.panels = [...this.querySelectorAll('[data-builder-video-panel]')];

      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-builder-video-index]');
        if (button) this.show(Number(button.dataset.builderVideoIndex));
      }, { signal: this.controller.signal });

      this.addEventListener('keydown', (event) => {
        const button = event.target.closest('[data-builder-video-index]');
        if (!button || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const currentIndex = this.buttons.indexOf(button);
        let nextIndex = currentIndex;
        if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = this.buttons.length - 1;
        else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % this.buttons.length;
        else nextIndex = (currentIndex - 1 + this.buttons.length) % this.buttons.length;
        this.show(nextIndex, true);
      }, { signal: this.controller.signal });

      this.show(Math.max(0, this.buttons.findIndex((button) => button.getAttribute('aria-selected') === 'true')));
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    show(index, focus = false) {
      if (!this.panels.length) return;
      const nextIndex = Math.min(this.panels.length - 1, Math.max(0, index));
      this.buttons.forEach((button, buttonIndex) => {
        const active = buttonIndex === nextIndex;
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
        if (active && focus) button.focus();
      });
      this.panels.forEach((panel, panelIndex) => {
        const active = panelIndex === nextIndex;
        const video = panel.querySelector('video');
        panel.hidden = !active;
        panel.inert = !active;
        if (!active) video?.pause();
      });
    }
  }

  if (!customElements.get('enhancement-promos')) customElements.define('enhancement-promos', EnhancementPromos);
  if (!customElements.get('enhancement-color-browser')) customElements.define('enhancement-color-browser', EnhancementColorBrowser);
  if (!customElements.get('enhancement-size-group')) customElements.define('enhancement-size-group', EnhancementSizeGroup);
  if (!customElements.get('enhancement-steps')) customElements.define('enhancement-steps', EnhancementSteps);
  if (!customElements.get('builder-video-gallery')) customElements.define('builder-video-gallery', BuilderVideoGallery);
})();
